import React, { useEffect, useMemo, useRef, useState } from 'react';
import Board from '@cloudscape-design/board-components/board';
import { Box, Button, SpaceBetween } from '@cloudscape-design/components';
import BudgetPlanningWidget from './widgets/BudgetPlanningWidget';

const STORAGE_KEY = 'finance-budget-planning-layout-v2';
const widgetRegistry = {
  'planning': {
    title: 'Budget entry',
    description: 'Enter and review a fiscal year’s budget.',
    component: BudgetPlanningWidget,
    definition: { defaultRowSpan: 9, defaultColumnSpan: 4, minRowSpan: 4, minColumnSpan: 2 },
  },
};
const defaultLayout = [{ id: 'planning', rowSpan: 9, columnSpan: 4 }];
const exportLayout = items => items.map(({ id, rowSpan, columnSpan, columnOffset }) => ({ id, rowSpan, columnSpan, columnOffset }));
function loadLayout() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(saved)) {
      const seen = new Set();
      return saved.filter(item => {
        if (!widgetRegistry[item?.id] || seen.has(item.id)) return false;
        seen.add(item.id); return true;
      }).map(item => ({
        id: item.id,
        rowSpan: Number.isInteger(item.rowSpan) && item.rowSpan >= 4 ? item.rowSpan : 9,
        columnSpan: Number.isInteger(item.columnSpan) && item.columnSpan >= 2 && item.columnSpan <= 4 ? item.columnSpan : 4,
        columnOffset: item.columnOffset,
      }));
    }
  } catch (_) { /* Use the default layout when storage is unavailable. */ }
  return defaultLayout;
}

const boardI18nStrings = {
  liveAnnouncementDndStarted: operation => (operation === "resize" ? "Resizing" : "Dragging"),
  liveAnnouncementDndItemReordered: operation => {
    const position = operation.direction === "horizontal"
      ? `column ${operation.placement.x + 1}`
      : `row ${operation.placement.y + 1}`;
    return `Item moved to ${position}.`;
  },
  liveAnnouncementDndItemResized: operation => {
    const base = operation.direction === "horizontal"
      ? `columns ${operation.placement.width}`
      : `rows ${operation.placement.height}`;
    const constraint = operation.direction === "horizontal"
      ? (operation.isMinimalColumnsReached ? " (minimal)" : "")
      : (operation.isMinimalRowsReached ? " (minimal)" : "");
    return `Item resized to ${base}${constraint}.`;
  },
  liveAnnouncementDndItemInserted: operation => {
    const column = `column ${operation.placement.x + 1}`;
    const row = `row ${operation.placement.y + 1}`;
    return `Item inserted to ${column}, ${row}.`;
  },
  liveAnnouncementDndCommitted: operation => `${operation} committed`,
  liveAnnouncementDndDiscarded: operation => `${operation} discarded`,
  liveAnnouncementItemRemoved: op => `Removed item ${op.item.data.title}.`,
  navigationAriaLabel: "Budget Planning dashboard navigation",
  navigationAriaDescription: "Use arrow keys to move between widgets on the Budget Planning dashboard.",
  navigationItemAriaLabel: item => (item ? item.data.title : "Empty"),
};

export default function FinanceBudgetsPage({ updateBreadcrumbs, setAvailableItems, setSplitPanelOpen, toggleHelpPanel }) {
  const [layout, setLayout] = useState(loadLayout);
  const paletteSignature = useRef(null);
  const layoutSignature = useRef(null);
  const items = useMemo(() => layout.map(item => ({ ...item,
    definition: widgetRegistry[item.id].definition,
    data: { title: widgetRegistry[item.id].title, description: widgetRegistry[item.id].description },
  })), [layout]);
  const palette = useMemo(() => Object.entries(widgetRegistry)
    .filter(([id]) => !layout.some(item => item.id === id))
    .map(([id, widget]) => ({ id, definition: widget.definition,
      data: { title: widget.title, description: widget.description } })), [layout]);

  useEffect(() => {
    updateBreadcrumbs?.([{ text: 'Home', href: '/' },
      { text: 'Budgets and Finance', href: '/finance/overview' }, { text: 'Budget Planning', href: '/finance/budgets/planning' }]);
  }, [updateBreadcrumbs]);
  useEffect(() => {
    const nextPalette = JSON.stringify(palette);
    if (nextPalette !== paletteSignature.current) {
      paletteSignature.current = nextPalette;
      setAvailableItems?.(palette);
    }
    const nextLayout = JSON.stringify(layout);
    if (nextLayout !== layoutSignature.current) {
      layoutSignature.current = nextLayout;
      try { localStorage.setItem(STORAGE_KEY, nextLayout); } catch (_) { /* The board remains usable without storage. */ }
    }
  }, [layout, palette, setAvailableItems]);
  useEffect(() => {
    const add = event => {
      const id = event.detail?.id;
      const widget = widgetRegistry[id];
      if (!widget) return;
      setLayout(previous => previous.some(item => item.id === id) ? previous : [...previous,
        { id, rowSpan: widget.definition.defaultRowSpan, columnSpan: widget.definition.defaultColumnSpan }]);
    };
    const open = () => { setAvailableItems?.(palette); setSplitPanelOpen?.(true); };
    const reset = () => setLayout(previous => JSON.stringify(previous) === JSON.stringify(defaultLayout) ? previous : defaultLayout);
    window.addEventListener('palette:add', add);
    window.addEventListener('financeBudgets:openPalette', open);
    window.addEventListener('financeBudgets:resetLayout', reset);
    return () => {
      window.removeEventListener('palette:add', add);
      window.removeEventListener('financeBudgets:openPalette', open);
      window.removeEventListener('financeBudgets:resetLayout', reset);
    };
  }, [palette, setAvailableItems, setSplitPanelOpen]);

  return <Board items={items} i18nStrings={boardI18nStrings}
    onItemsChange={({ detail }) => {
      const next = exportLayout(detail.items.filter(item => widgetRegistry[item.id]));
      setLayout(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    }}
    renderItem={(item, actions) => {
      const Widget = widgetRegistry[item.id].component;
      return <Widget actions={actions} toggleHelpPanel={toggleHelpPanel} />;
    }}
    empty={<Box padding="m"><SpaceBetween size="s">
      <Box>No widgets on the Budget Planning dashboard. Use Add widget to choose a widget.</Box>
      <Button onClick={() => window.dispatchEvent(new CustomEvent('financeBudgets:resetLayout'))}>Restore defaults</Button>
    </SpaceBetween></Box>} />;
}
